import React, { useEffect, useState } from 'react';
import { DollarSign, ShoppingBag, Users, ArrowUpRight } from 'lucide-react';
import './Dashboard.css';
import dashboardService, { DashboardStats } from '../services/dashboardService';
import { productService } from '../services/productService';

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [lowStockProducts, setLowStockProducts] = useState<any[]>([]); // Using any for simplicity or update types
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [funcStats, funcLowStock] = await Promise.all([
          dashboardService.getStats(),
          productService.getLowStockProducts()
        ]);
        setStats(funcStats);
        setLowStockProducts(funcLowStock);
      } catch (error) {
        console.error("Error fetching dashboard stats:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-DO', {
      style: 'currency',
      currency: 'DOP',
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="loader-container">
          <p>Cargando dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="dashboard-grid">
        {/* Stats Cards */}
        <div className="stat-card card">
          <div className="stat-header">
            <div>
              <p className="stat-title">Ventas Totales</p>
              <h3 className="stat-value">{formatCurrency(stats?.totalSales || 0)}</h3>
            </div>
            <div className="stat-icon bg-green-100 text-green-600">
              <DollarSign size={24} />
            </div>
          </div>
          <p className="stat-trend text-green-600">
            <ArrowUpRight size={16} /> +0% vs mes anterior
          </p>
        </div>

        <div className="stat-card card">
          <div className="stat-header">
            <div>
              <p className="stat-title">Ordenes</p>
              <h3 className="stat-value">{stats?.totalOrders || 0}</h3>
            </div>
            <div className="stat-icon bg-blue-100 text-blue-600">
              <ShoppingBag size={24} />
            </div>
          </div>
          <p className="stat-trend text-blue-600">
            <ArrowUpRight size={16} /> +0% vs mes anterior
          </p>
        </div>

        <div className="stat-card card">
          <div className="stat-header">
            <div>
              <p className="stat-title">Clientes</p>
              <h3 className="stat-value">{stats?.totalCustomers || 0}</h3>
            </div>
            <div className="stat-icon bg-orange-100 text-orange-600">
              <Users size={24} />
            </div>
          </div>
          <p className="stat-trend text-slate-500">
            0% vs mes anterior
          </p>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="recent-section">
        <h3>Ventas Recientes</h3>
        <div className="card table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>ID Orden</th>
                <th>Cliente</th>
                <th>Fecha</th>
                <th>Total</th>
                <th>Estado</th>
              </tr>
            </thead>
            <tbody>
              {stats?.recentSales && stats.recentSales.length > 0 ? (
                stats.recentSales.map((sale) => (
                  <tr key={sale.id}>
                    <td>#ORD-{sale.id.toString().padStart(3, '0')}</td>
                    <td>{sale.customerName}</td>
                    <td>{new Date(sale.saleDate).toLocaleDateString()}</td>
                    <td>{formatCurrency(sale.totalAmount)}</td>
                    <td>
                      <span className={`badge ${sale.status === 'Completado' ? 'badge-success' : 'badge-warning'}`}>
                        {sale.status}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} style={{ textAlign: 'center', padding: '2rem' }}>
                    No hay ventas registradas
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Low Stock Section */}
      <div className="recent-section">
        <h3>Alertas de Stock Bajo</h3>
        <div className="card table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Producto</th>
                <th>Categoría</th>
                <th>Stock Actual</th>
                <th>Mínimo</th>
                <th>Estado</th>
              </tr>
            </thead>
            <tbody>
              {lowStockProducts.length > 0 ? (
                lowStockProducts.map((product: any) => (
                  <tr key={product.id}>
                    <td>{product.name}</td>
                    <td>{product.category?.name || 'N/A'}</td>
                    <td className="text-red-600 font-bold">{product.stock}</td>
                    <td>{product.minStockThreshold}</td>
                    <td>
                      <span className="badge badge-warning">Stock Crítico</span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} style={{ textAlign: 'center', padding: '2rem' }}>
                    No hay alertas de stock
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default Dashboard;